import { motion, type Variants } from 'motion/react';
import type { ReactNode } from 'react';

// ====== Motion Variants ======

const wrapperVariants: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  show: { opacity: 1, scale: 1 },
};

const tableBodyVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.02,
    },
  },
};

const tableRowVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

// ====== Animated Table Wrapper ======

interface AnimatedTableWrapperProps {
  children: ReactNode;
  className?: string;
}

function AnimatedTableWrapper({ children, className }: AnimatedTableWrapperProps) {
  return (
    <motion.div
      variants={wrapperVariants}
      initial="hidden"
      animate="show"
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ====== Animated Table Body ======

interface AnimatedTableBodyProps {
  children: ReactNode;
  className?: string;
}

function AnimatedTableBody({ children, className }: AnimatedTableBodyProps) {
  return (
    <motion.tbody
      variants={tableBodyVariants}
      initial="hidden"
      animate="show"
      className={className}
    >
      {children}
    </motion.tbody>
  );
}

// ====== Animated Table Row ======

interface AnimatedTableRowProps {
  children: ReactNode;
  className?: string;
  index: number;
  onClick?: () => void;
}

function AnimatedTableRow({ children, className, index, onClick }: AnimatedTableRowProps) {
  return (
    <motion.tr
      variants={tableRowVariants}
      initial="hidden"
      animate="visible"
      transition={{
        duration: 0.35,
        ease: 'easeOut',
        delay: Math.min(index * 0.02, 0.6),
      }}
      className={className}
      onClick={onClick}
    >
      {children}
    </motion.tr>
  );
}

// ====== Exports ======

export { AnimatedTableWrapper, AnimatedTableBody, AnimatedTableRow };
